import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';
import {
  getClassroom,
  getClassrooms,
  getCampusClassrooms,
  createClassroom,
  updateClassroom,
  deleteClassroom,
} from '../api/classrooms.api';
import {
  IClassroom,
  IClassroomList,
  IGetClassroom,
  IGetClassrooms,
  IClassroomReq,
} from '../models/classrooms.model';

interface IClassroomState {
  classroom?: IClassroom;
  classrooms?: IClassroomList[];
  isLoading: boolean;
  error?: AxiosError | string;
}

const initialState: IClassroomState = {
  classroom: undefined,
  classrooms: [],
  isLoading: false,
};

const classroomSlicer = createSlice({
  name: 'classroom',
  initialState,
  reducers: {
    getClassroomStart: (state: IClassroomState) => {
      state.classroom = undefined;
      state.isLoading = true;
      state.error = undefined;
    },
    getClassroomSuccess: (
      state: IClassroomState,
      action: PayloadAction<IGetClassroom>,
    ) => {
      state.classroom = action.payload.data;
      state.isLoading = false;
      state.error = undefined;
    },
    getClassroomFailure: (
      state: IClassroomState,
      action: PayloadAction<AxiosError | string>,
    ) => {
      state.classroom = undefined;
      state.isLoading = false;
      state.error = action.payload;
    },
    getClassroomsStart: (state: IClassroomState) => {
      state.classrooms = [];
      state.isLoading = true;
      state.error = undefined;
    },
    getClassroomsSuccess: (
      state: IClassroomState,
      action: PayloadAction<IGetClassrooms>,
    ) => {
      state.classrooms = action.payload.data;
      state.isLoading = false;
      state.error = undefined;
    },
    getClassroomsFailure: (
      state: IClassroomState,
      action: PayloadAction<AxiosError | string>,
    ) => {
      state.classrooms = [];
      state.isLoading = false;
      state.error = action.payload;
    },
    requestStart: (state: IClassroomState) => {
      state.isLoading = true;
      state.error = undefined;
    },
    requestFailure: (
      state: IClassroomState,
      action: PayloadAction<AxiosError | string>,
    ) => {
      state.isLoading = false;
      state.error = action.payload;
    },
    createSuccess: (state: IClassroomState) => {
      state.isLoading = false;
      state.error = undefined;
    },
    updateSuccess: (state: IClassroomState) => {
      state.isLoading = false;
      state.error = undefined;
    },
    deleteSuccess: (state: IClassroomState) => {
      state.isLoading = false;
      state.error = undefined;
    },
  },
});

export const {
  getClassroomsStart,
  getClassroomsSuccess,
  getClassroomsFailure,
  getClassroomStart,
  getClassroomSuccess,
  getClassroomFailure,
  requestStart,
  requestFailure,
  createSuccess,
  updateSuccess,
  deleteSuccess,
} = classroomSlicer.actions;

export default classroomSlicer.reducer;

export const doGetClassroom =
  (id: string): any =>
  async (dispatch: any) => {
    try {
      dispatch(getClassroomStart());
      const response = await getClassroom(id);
      dispatch(getClassroomSuccess(response.data));
      return response;
    } catch (e: any) {
      dispatch(getClassroomFailure(e.response?.data?.data || e.message));
      return e.response;
    }
  };

export const doGetClassrooms = (): any => async (dispatch: any) => {
  try {
    dispatch(getClassroomsStart());
    const response = await getClassrooms();
    dispatch(getClassroomsSuccess(response.data));
    return response;
  } catch (e: any) {
    dispatch(getClassroomsFailure(e.response?.data?.data || e.message));
    return e.response;
  }
};

export const doGetCampusClassrooms =
  (campusCode: string): any =>
  async (dispatch: any) => {
    try {
      dispatch(getClassroomsStart());
      const response = await getCampusClassrooms(campusCode);
      dispatch(getClassroomsSuccess(response.data));
      return response;
    } catch (e: any) {
      dispatch(getClassroomsFailure(e.response?.data?.data || e.message));
      return e.response;
    }
  };

export const doCreateClassroom =
  (campusCode: string, data: IClassroomReq): any =>
  async (dispatch: any) => {
    try {
      dispatch(requestStart());
      const response = await createClassroom(campusCode, data);
      dispatch(createSuccess());
      return response;
    } catch (e: any) {
      dispatch(requestFailure(e.response?.data?.data || e.message));
      return e.response;
    }
  };

export const doUpdateClassroom =
  (id: string, data: IClassroomReq): any =>
  async (dispatch: any) => {
    try {
      dispatch(requestStart());
      const response = await updateClassroom(id, data);
      dispatch(updateSuccess());
      return response;
    } catch (e: any) {
      dispatch(requestFailure(e.response?.data?.data || e.message));
      return e.response;
    }
  };

export const doDeleteClassroom =
  (id: string): any =>
  async (dispatch: any) => {
    try {
      dispatch(requestStart());
      const response = await deleteClassroom(id);
      dispatch(deleteSuccess());
      return response;
    } catch (e: any) {
      dispatch(requestFailure(e.response?.data?.data || e.message));
      return e.response;
    }
  };
